import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { Mic2, History, Users as UsersIcon, Activity, Volume2, LogOut, Menu, X } from 'lucide-react';
import { AuthProvider, useAuth } from './lib/auth-context';
import InAppBrowserWarning from './components/InAppBrowserWarning';
import Login from './pages/Login';
import Studio from './pages/Studio';
import MyHistory from './pages/MyHistory';
import Voices from './pages/Voices';
import Users from './pages/Users';
import ActivityLog from './pages/ActivityLog';

export default function App() {
  return (
    <AuthProvider>
      {/* In-app tarayıcı (Instagram/WhatsApp vb.) uyarısı — login dahil her ekranda */}
      <InAppBrowserWarning />
      <Shell />
    </AuthProvider>
  );
}

function Shell() {
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Sayfa değişince mobil çekmeceyi kapat.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  if (auth.loading) {
    return <div className="flex items-center justify-center min-h-screen text-slate-500">Yükleniyor…</div>;
  }
  if (!auth.email) {
    return <Login />;
  }
  if (!auth.adminRow) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-700 text-center p-8">
        <div>
          <p className="font-semibold mb-2">{auth.email}</p>
          <p>Bu hesap admin paneline tanımlı değil. Master admin'e (Fikret) bildirin.</p>
          <button onClick={auth.signOut} className="mt-4 px-4 py-2 bg-slate-200 rounded">Çıkış</button>
        </div>
      </div>
    );
  }
  if (!auth.adminRow.is_active) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-700 text-center p-8">
        <div>
          <p className="font-semibold mb-2">Hesap pasif</p>
          <p>Hesabın master tarafından devre dışı bırakılmış.</p>
          <button onClick={auth.signOut} className="mt-4 px-4 py-2 bg-slate-200 rounded">Çıkış</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen md:flex">
      {/* Mobil üst bar — sadece < md görünür */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[color:var(--color-primary)] text-white px-4 py-3">
        <h1 className="text-base font-bold">🎙️ Seslendirme</h1>
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="Menüyü aç"
          className="p-1.5 rounded hover:bg-white/10"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Çekmece açıkken karartma katmanı — tıklayınca kapatır */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 flex flex-col bg-[color:var(--color-primary)] text-white p-4
          transform transition-transform duration-200
          md:sticky md:top-0 md:z-auto md:h-screen md:w-56 md:translate-x-0 md:transition-none
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="mb-6 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-bold">🎙️ Seslendirme</h1>
            <p className="text-xs opacity-70 break-all">{auth.email}{auth.isMaster ? ' · master' : ''}</p>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Menüyü kapat"
            className="md:hidden p-1 rounded hover:bg-white/10 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="space-y-1">
          <NavItem to="/" icon={<Mic2 size={16} />} label="Stüdyo" />
          <NavItem to="/history" icon={<History size={16} />} label="Geçmişim" />
          {auth.isMaster && (
            <>
              <div className="mt-4 pt-4 border-t border-white/10 text-xs uppercase opacity-50 px-2 mb-1">Yönetim</div>
              <NavItem to="/voices" icon={<Volume2 size={16} />} label="Sesler" />
              <NavItem to="/users" icon={<UsersIcon size={16} />} label="Kullanıcılar" />
              <NavItem to="/activity" icon={<Activity size={16} />} label="Activity log" />
            </>
          )}
        </nav>
        <div className="mt-auto pt-6">
          <button
            onClick={auth.signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-white/10"
          >
            <LogOut size={14} /> Çıkış
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-auto">
        <Routes>
          <Route path="/" element={<Studio />} />
          <Route path="/history" element={<MyHistory />} />
          <Route path="/voices" element={auth.isMaster ? <Voices /> : <Navigate to="/" replace />} />
          <Route path="/users" element={auth.isMaster ? <Users /> : <Navigate to="/" replace />} />
          <Route path="/activity" element={auth.isMaster ? <ActivityLog /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
          isActive ? 'bg-white/15' : 'hover:bg-white/10'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
